/**
 * Gift Selection Hook for Cart Item Reference (Product-Level Gifts in Cart)
 *
 * This hook manages gift selection for a specific cart item reference.
 * Unlike useGiftSelection (which uses productId-based sessions), this hook:
 * - Uses cartItemReferenceId as the session identifier
 * - Initializes with existing gift selections from cartItemReference.logics[].actions[]
 * - Updates the cart item reference logics when gifts are confirmed
 *
 * IMPORTANT: This is for product-level gifts that are attached to a cart item,
 * NOT for cart-level gifts (which use useCartGiftSelection).
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from '@legendapp/state/react';
import { type FreeGiftsData } from '../../../types/front.graphql';
import { type SelectedGift } from '../types/gift-selection.types';
import { type GiftSelectionSession } from '../stores/simple-gift-selection.store';
import { giftSelectionStoreAPI } from '../stores/simple-gift-selection-store.public-api';
import {
    type CartItemReference,
    type CartItemReferenceLogic,
    type CartItemReferenceAction,
    ActionType,
    LogicType,
} from '@kidslabs/module-quote';
import type { Observable } from '@legendapp/state';

export interface UseCartItemGiftSelectionProps {
    /**
     * The cart item reference that contains the product and its gift selections
     */
    cartItemReference: CartItemReference;
    /**
     * Available gifts for this product (from useProductGiftHook)
     */
    availableGifts: FreeGiftsData[];
    /**
     * Replacement mode for "Đổi quà" functionality
     * When true, skips remaining quantity filtering
     * @default false
     */
    isReplacementMode?: boolean;

    /**
     * Session ID for this cart item reference
     */
    sessionId: string;
}

export interface UseCartItemGiftSelectionReturn {
    /**
     * Currently selected gifts
     */
    selectedGifts: SelectedGift[];
    /**
     * Select a gift (replaces existing gift in same rule)
     */
    selectGift: (gift: SelectedGift) => void;
    /**
     * Remove a gift by ID
     */
    removeGift: (giftId: string) => void;
    /**
     * Update gift quantity
     */
    updateQuantity: (giftId: string, quantity: number) => void;
    /**
     * Whether gift selection is complete
     */
    isComplete: boolean;
    /**
     * Session store observable for reactive updates
     */
    store$?: Observable<GiftSelectionSession>;
    /**
     * Session ID for this cart item reference
     */
    sessionId: string;
    /**
     * Replacement mode flag - passed through from props
     */
    isReplacementMode: boolean;
}

/**
 * Parameters for converting CartItemReferenceAction to SelectedGift format
 */
interface ConvertActionToSelectedGiftParams {
    logic: CartItemReferenceLogic;
    action: CartItemReferenceAction;
    availableGifts: FreeGiftsData[];
    sessionId: string;
}

/**
 * Convert CartItemReferenceAction to SelectedGift format
 */
const convertActionToSelectedGift = ({
    logic,
    action,
    availableGifts,
    sessionId,
}: ConvertActionToSelectedGiftParams): SelectedGift | null => {
    if (action.actionType !== ActionType.GIFT || !action.actionProduct) {
        return null;
    }

    // Find matching gift data from available gifts
    const matchingGiftData = availableGifts.find(gd => gd.rule?.rule_id === logic.logicId);

    // If no matching gift data, we can't create a valid SelectedGift
    if (!matchingGiftData) {
        return null;
    }

    return {
        sessionId,
        id: `${logic.logicId}-${action.actionProduct.id}-cart`,
        ruleId: logic.logicId,
        productId: action.actionProduct.id ?? 0,
        item: action.actionProduct,
        giftData: matchingGiftData,
        quantity: action.actionQty ?? 1,
        giftSlots: action.actionQty ?? 1,
        quantityUsedByRule: logic.allocatedQty ?? 0,
        selectionRound: 1,
        selectedAt: Date.now(),
    };
};

/**
 * Parameters for extracting gifts from cart item reference
 */
interface ExtractGiftsFromCartItemReferenceParams {
    cartItemReference: CartItemReference;
    availableGifts: FreeGiftsData[];
    sessionId: string;
}

/**
 * Extract existing gift selections from cart item reference
 */
const extractGiftsFromCartItemReference = ({
    cartItemReference,
    availableGifts,
    sessionId,
}: ExtractGiftsFromCartItemReferenceParams): SelectedGift[] => {
    const gifts: SelectedGift[] = [];

    cartItemReference.logics?.forEach(logic => {
        if (logic.logicType === LogicType.PROMOTION) {
            logic.actions?.forEach(action => {
                const gift = convertActionToSelectedGift({
                    logic,
                    action,
                    availableGifts,
                    sessionId,
                });
                if (gift) {
                    gifts.push(gift);
                }
            });
        }
    });

    return gifts;
};

/**
 * Hook for managing gift selection for a specific cart item reference
 */
export const useCartItemGiftSelection = ({
    cartItemReference,
    availableGifts,
    isReplacementMode = false,
    sessionId,
}: UseCartItemGiftSelectionProps): UseCartItemGiftSelectionReturn => {
    const cartItemReferenceId = cartItemReference.cartItemReferenceId;
    const productQuantity = cartItemReference.qty;
    const productId = cartItemReference.productId;

    // Track if we've initialized from cart item reference for this specific session
    const initializedSessionRef = useRef<string | null>(null);

    // Get session observable, restoring from server data if the session doesn't exist yet.
    // After server sync, logic.sessionId is populated but the giftSelectionStore$ has no
    // corresponding entry. We create a minimal session so the UI can bind to it.
    const store$ = useMemo(() => {
        if (!sessionId) return undefined;

        const existingStore = giftSelectionStoreAPI.state.getSessionObservable(sessionId);
        if (existingStore) return existingStore;

        // Session missing — restore a placeholder from server-synced sessionId
        giftSelectionStoreAPI.actions.restoreServerSession(
            sessionId,
            String(productId ?? cartItemReferenceId),
            'product',
            productQuantity
        );
        return giftSelectionStoreAPI.state.getSessionObservable(sessionId);
    }, [sessionId, productId, cartItemReferenceId, productQuantity]);

    // Subscribe to selected gifts reactively
    const selectedGifts = useSelector((): SelectedGift[] => store$?.selectedGifts.get() ?? []);

    // Initialize/restore session with existing gifts from cart item reference
    // This handles both first-time initialization and restoration when session was deleted
    useEffect(() => {
        if (!sessionId) return;
        if (!store$) {
            return;
        }

        if (initializedSessionRef.current === sessionId) {
            // Check if we've already initialized for this specific session
            // Already initialized for this session, but check if session has no gifts and cartItemReference has gifts
            // This handles the case where session was cleared but cartItemReference still has data

            /**
             * @deprecated don't handle this case
             */

            return;
        }

        // New session or different session - initialize from cart item reference
        const existingGifts = extractGiftsFromCartItemReference({
            cartItemReference,
            availableGifts,
            sessionId,
        });

        if (existingGifts.length > 0) {
            // Restore existing gifts from cart item reference to the session
            /**
             * @deprecated don't handle this case
             */
            //giftSelectionStoreAPI.actions.setSelectedGifts(sessionId, existingGifts);
        }

        // Mark this session as initialized
        initializedSessionRef.current = sessionId;

        // Update session with product quantity and available gifts
        giftSelectionStoreAPI.actions.updateSession(sessionId, {
            productQuantity,
            availableGifts,
        });
    }, [sessionId, cartItemReference, availableGifts, productQuantity, cartItemReferenceId, store$]);

    // Select a gift (replaces existing gift in same selection round)
    const selectGift = useCallback(
        (gift: SelectedGift) => {
            if (!sessionId) return;

            const currentGifts = giftSelectionStoreAPI.state.getSelectedGifts(sessionId) as SelectedGift[];

            // Check if this exact gift is already selected
            const existingGiftIndex = currentGifts.findIndex(g => g.id === gift.id);

            console.log('run selectGift in useCartItemGiftSelection with data: ', {
                existingGiftIndex: existingGiftIndex,
            });

            if (existingGiftIndex >= 0) {
                // Same gift clicked - deselect it
                giftSelectionStoreAPI.actions.setSelectedGifts(
                    sessionId,
                    currentGifts.filter(g => g.id !== gift.id)
                );
                return;
            }

            // For cart item gifts: Replace gift with same ruleId
            const giftsFromOtherRules = currentGifts.filter(g => g.ruleId !== gift.ruleId);
            giftSelectionStoreAPI.actions.setSelectedGifts(sessionId, [...giftsFromOtherRules, gift]);
        },
        [sessionId]
    );

    // Remove a gift
    const removeGift = useCallback(
        (giftId: string) => {
            if (!sessionId) return;
            giftSelectionStoreAPI.actions.removeGift(sessionId, giftId);
        },
        [sessionId]
    );

    // Update gift quantity
    const updateQuantity = useCallback(
        (giftId: string, quantity: number) => {
            if (!sessionId) return;

            console.log('run updateQuantity in useCartItemGiftSelection with data: ', {
                giftId: giftId,
            });

            const currentGifts = giftSelectionStoreAPI.state.getSelectedGifts(sessionId) as SelectedGift[];
            const updatedGifts = currentGifts.map(g => (g.id === giftId ? { ...g, quantity, giftSlots: quantity } : g));
            giftSelectionStoreAPI.actions.setSelectedGifts(sessionId, updatedGifts);
        },
        [sessionId]
    );

    // Check if selection is complete
    const isComplete = useMemo(() => {
        return selectedGifts.length > 0;
    }, [selectedGifts]);

    return {
        selectedGifts,
        selectGift,
        removeGift,
        updateQuantity,
        isComplete,
        store$,
        sessionId,
        isReplacementMode,
    };
};
